import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BellRing, CheckCircle2, CreditCard, MessageSquareMore, QrCode, Smartphone, Sparkles } from 'lucide-react';

export function MobileSellerMockup() {
  return (
    <div className="w-[280px] rounded-[2.2rem] border border-slate-300/90 bg-white p-3 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.55)]">
      <div className="rounded-[1.8rem] border border-slate-200/90 bg-slate-50/90 p-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Seller Studio</p>
          <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            Live
          </span>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
          <p className="text-xs text-slate-500">Quote total</p>
          <p className="mt-1 text-3xl font-extrabold text-slate-900">$4,500</p>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm font-semibold text-slate-700">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '$'].map((key) => (
            <button
              key={key}
              className="rounded-xl border border-slate-200/90 bg-white py-2 transition hover:border-slate-300 hover:bg-slate-50 hover:shadow-[0_8px_16px_-14px_rgba(15,23,42,0.35)]"
              type="button"
            >
              {key}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="mt-4 w-full rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white shadow-[0_12px_24px_-16px_rgba(5,150,105,0.9)] transition hover:scale-[1.01] hover:bg-emerald-500 hover:shadow-[0_18px_28px_-16px_rgba(5,150,105,0.95)]"
        >
          Generate Link
        </button>
      </div>
    </div>
  );
}

export function CheckoutMockup() {
  return (
    <div className="w-full max-w-md rounded-[2rem] border border-slate-300/90 bg-white p-3 shadow-[0_36px_80px_-40px_rgba(15,23,42,0.6)]">
      <div className="rounded-[1.6rem] border border-slate-200/90 bg-slate-50/90 p-5">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="font-semibold uppercase tracking-[0.14em]">Secure checkout</span>
          <span className="inline-flex items-center gap-1 font-medium text-slate-700">
            <CreditCard className="h-3.5 w-3.5" /> Stripe
          </span>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200/90 bg-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
          <p className="text-xs text-slate-500">Kitchen remodel quote</p>
          <p className="mt-1 text-2xl font-extrabold text-slate-900">$4,500.00</p>
        </div>

        <div className="mt-4 space-y-2">
          <div className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm text-slate-700">
            Pay in 4 with Klarna
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-sm font-semibold text-emerald-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
            Pay monthly with Affirm
          </div>
          <div className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm text-slate-700">
            Pay now with card
          </div>
        </div>

        <button
          className="mt-4 w-full rounded-xl bg-slate-900 py-3 text-sm font-bold text-white shadow-[0_12px_24px_-18px_rgba(15,23,42,0.95)] transition hover:scale-[1.01] hover:bg-slate-800 hover:shadow-[0_18px_30px_-18px_rgba(15,23,42,0.95)]"
          type="button"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export function BuyerHandoffMockup() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Buyer handoff</p>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.35 }}
        className="mt-3 max-w-sm rounded-2xl bg-slate-900 p-3 text-sm text-white"
      >
        Here&apos;s the link to finalize your kitchen remodel: lnd.li/xyz
      </motion.div>
      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
        <MessageSquareMore className="h-3.5 w-3.5" /> Sent in 1 tap
      </div>
    </div>
  );
}

export function AhaToastMockup() {
  return (
    <div className="inline-flex items-start gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 shadow-[0_10px_25px_-15px_rgba(5,150,105,0.7)]">
      <BellRing className="mt-0.5 h-4 w-4 text-emerald-600" />
      <div>
        <p className="text-xs font-semibold text-slate-900">Client viewed your $4,500 quote</p>
        <p className="text-xs text-slate-600">Send a follow-up now while intent is hot.</p>
      </div>
      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
    </div>
  );
}

export function HeroDealFlowMockup() {
  const [phase, setPhase] = useState(0);
  const [typedIndex, setTypedIndex] = useState(0);
  const entryDigits = ['4', '5', '0', '0'];

  useEffect(() => {
    const phaseTimer = window.setInterval(() => {
      setPhase((prev) => (prev + 1) % 4);
    }, 2600);
    return () => window.clearInterval(phaseTimer);
  }, []);

  useEffect(() => {
    if (phase !== 0) {
      setTypedIndex(entryDigits.length);
      return;
    }
    setTypedIndex(0);
    let idx = 0;
    const typingTimer = window.setInterval(() => {
      idx += 1;
      setTypedIndex(Math.min(idx, entryDigits.length));
      if (idx >= entryDigits.length) window.clearInterval(typingTimer);
    }, 250);
    return () => window.clearInterval(typingTimer);
  }, [phase]);

  const typedTotal = useMemo(() => {
    if (typedIndex === 0) return '$0';
    const value = entryDigits.slice(0, typedIndex).join('').padEnd(entryDigits.length, '0');
    return `$${Number(value).toLocaleString()}`;
  }, [typedIndex]);

  return (
    <div className="relative w-full max-w-xl">
      <div className="absolute -top-10 right-10 h-24 w-24 rounded-full bg-emerald-300/40 blur-2xl" />
      <div className="absolute -bottom-12 left-8 h-24 w-24 rounded-full bg-amber-200/35 blur-2xl" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative ml-auto w-[88%] rounded-[2rem] border border-slate-300/90 bg-white p-3 shadow-[0_36px_80px_-40px_rgba(15,23,42,0.6)]"
      >
        <div className="rounded-[1.6rem] border border-slate-200/90 bg-slate-50/90 p-5">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-[0.14em]">Secure checkout</span>
            <span className="inline-flex items-center gap-1 font-medium text-slate-700">
              <CreditCard className="h-3.5 w-3.5" /> Stripe
            </span>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200/90 bg-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
            <p className="text-xs text-slate-500">Kitchen remodel quote</p>
            <p className="mt-1 text-2xl font-extrabold text-slate-900">$4,500.00</p>
          </div>

          <div className="mt-4 space-y-2">
            <div className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm text-slate-700">Pay in 4 with Klarna</div>
            <motion.div
              animate={{ scale: phase === 1 ? [1, 1.02, 1] : 1 }}
              transition={{ duration: 0.45 }}
              className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-sm font-semibold text-emerald-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"
            >
              Pay monthly with Affirm
            </motion.div>
            <div className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm text-slate-700">Pay now with card</div>
          </div>

          <motion.div
            animate={{ opacity: phase >= 1 ? 1 : 0.4 }}
            className="mt-4 grid grid-cols-[1fr_auto] gap-3 rounded-xl border border-slate-200/90 bg-white p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Scan or tap link</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">lendlifted.com/q/9f42</p>
            </div>
            <motion.div
              animate={{ scale: phase === 2 ? [1, 1.08, 1] : 1 }}
              transition={{ duration: 0.7, repeat: phase === 2 ? Infinity : 0 }}
              className="rounded-lg border border-slate-200/90 bg-slate-50 p-2"
            >
              <QrCode className="h-8 w-8 text-slate-700" />
            </motion.div>
          </motion.div>

          <button
            className="mt-4 w-full rounded-xl bg-slate-900 py-3 text-sm font-bold text-white shadow-[0_12px_24px_-18px_rgba(15,23,42,0.95)] transition hover:bg-slate-800"
            type="button"
          >
            Continue
          </button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: -10, y: 8 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.45, delay: 0.15 }}
        className="absolute -bottom-4 -left-3 z-20 w-[280px] rounded-[2.2rem] border border-slate-300/90 bg-white p-3 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.55)] md:-bottom-8 md:-left-6"
      >
        <div className="rounded-[1.8rem] border border-slate-200/90 bg-slate-50/90 p-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Seller Studio</p>
            <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Live</span>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
            <p className="text-xs text-slate-500">Quote total</p>
            <p className="mt-1 text-3xl font-extrabold text-slate-900">{typedTotal}</p>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm font-semibold text-slate-700">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '$'].map((key) => (
              <motion.button
                key={key}
                animate={{ scale: entryDigits[typedIndex - 1] === key ? [1, 0.95, 1] : 1 }}
                transition={{ duration: 0.2 }}
                className="rounded-xl border border-slate-200/90 bg-white py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]"
                type="button"
              >
                {key}
              </motion.button>
            ))}
          </div>
          <button
            type="button"
            className="mt-4 w-full rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white shadow-[0_12px_24px_-16px_rgba(5,150,105,0.9)]"
          >
            Generate Link
          </button>
        </div>
      </motion.div>

      <motion.div
        animate={{
          opacity: phase === 2 ? 1 : 0,
          x: phase === 2 ? 0 : 10,
          y: phase === 2 ? 0 : 8,
        }}
        transition={{ duration: 0.35 }}
        className="absolute right-3 top-10 z-30 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-[0_14px_30px_-22px_rgba(15,23,42,0.45)] backdrop-blur-sm"
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <Smartphone className="h-3.5 w-3.5 text-emerald-600" /> Buyer opened link
        </div>
      </motion.div>

      <motion.div
        animate={{
          opacity: phase === 3 ? 1 : 0,
          y: phase === 3 ? 0 : 10,
          scale: phase === 3 ? 1 : 0.96,
        }}
        transition={{ duration: 0.35 }}
        className="absolute right-2 top-44 z-30 max-w-[220px] rounded-xl border border-emerald-200 bg-white px-3 py-2 shadow-[0_16px_35px_-24px_rgba(5,150,105,0.9)]"
      >
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 text-emerald-600" />
          <div>
            <p className="text-xs font-semibold text-slate-900">Sale complete</p>
            <p className="text-xs text-slate-600">You get paid upfront. Buyer pays over time.</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
